/**
 * OpenAI Text-to-Speech provider
 */

import OpenAI from 'openai';
import { BaseProvider } from '../../core/base-provider';
import {
  TTSOptions,
  AudioResponse,
  Voice,
  UsageStats,
  ProviderError,
  AudioFormat,
} from '../../core/types';

export interface OpenAITTSProvider extends BaseProvider {
  synthesize(text: string, options?: TTSOptions): Promise<AudioResponse>;
  getVoices(): Promise<Voice[]>;
  stream(text: string, options?: TTSOptions): Promise<ReadableStream<Uint8Array>>;
}

export class OpenAITTS extends BaseProvider implements OpenAITTSProvider {
  private client?: OpenAI;

  private static readonly VOICES: Voice[] = [
    { id: 'alloy', name: 'Alloy', gender: 'neutral' },
    { id: 'echo', name: 'Echo', gender: 'male' },
    { id: 'fable', name: 'Fable', gender: 'neutral' },
    { id: 'onyx', name: 'Onyx', gender: 'male' },
    { id: 'nova', name: 'Nova', gender: 'female' },
    { id: 'shimmer', name: 'Shimmer', gender: 'female' },
  ];

  constructor() {
    super('openai-tts', 'OpenAI TTS', 'tts');
  }

  protected async onInitialize(): Promise<void> {
    if (!this.config.apiKey) {
      throw new ProviderError('OpenAI API key is required', this.id);
    }

    this.client = new OpenAI({
      apiKey: this.config.apiKey,
      baseURL: this.config.baseUrl,
      timeout: this.config.timeout || 60000, // Longer timeout for audio
      maxRetries: this.config.maxRetries || 3,
    });
  }

  protected async checkAvailability(): Promise<boolean> {
    try {
      // Test with a minimal request
      const response = await this.client!.audio.speech.create({
        model: 'tts-1',
        voice: 'alloy',
        input: 'test',
        response_format: 'mp3',
      });
      return response instanceof Response;
    } catch {
      return false;
    }
  }

  protected async fetchUsage(): Promise<UsageStats> {
    // OpenAI TTS is charged per character
    return {
      tokensUsed: 0,
      requestsCount: 0,
      cost: 0,
    };
  }

  async synthesize(text: string, options?: TTSOptions): Promise<AudioResponse> {
    this.ensureInitialized();

    try {
      const format = this.mapAudioFormat(options?.format || 'mp3');

      const response = await this.client!.audio.speech.create({
        model: options?.model || 'tts-1',
        voice: (options?.voice || 'alloy') as any,
        input: text,
        response_format: format as any,
        speed: options?.speed || 1.0,
      });

      const arrayBuffer = await response.arrayBuffer();

      return {
        audio: arrayBuffer,
        format: options?.format || 'mp3',
        voice: options?.voice || 'alloy',
      };
    } catch (error: any) {
      if (error.status === 429) {
        throw new ProviderError('Rate limit exceeded', this.id, {
          retryAfter: error.headers?.['retry-after'],
        });
      }
      throw new ProviderError(error.message || 'Failed to synthesize speech', this.id, error);
    }
  }

  async getVoices(): Promise<Voice[]> {
    this.ensureInitialized();
    return OpenAITTS.VOICES;
  }

  async stream(text: string, options?: TTSOptions): Promise<ReadableStream<Uint8Array>> {
    this.ensureInitialized();

    try {
      const format = this.mapAudioFormat(options?.format || 'mp3');

      const response = await this.client!.audio.speech.create({
        model: options?.model || 'tts-1',
        voice: (options?.voice || 'alloy') as any,
        input: text,
        response_format: format as any,
        speed: options?.speed || 1.0,
      });

      // OpenAI returns a Response object with a body stream
      if (!response.body) {
        throw new Error('No response body');
      }

      return response.body;
    } catch (error: any) {
      throw new ProviderError(error.message || 'Failed to stream speech', this.id, error);
    }
  }

  private mapAudioFormat(format: AudioFormat): string {
    // OpenAI supports: mp3, opus, aac, flac
    switch (format) {
      case 'mp3':
      case 'opus':
      case 'aac':
      case 'flac':
        return format;
      case 'wav':
      case 'ogg':
        // Default to mp3 for unsupported formats
        return 'mp3';
      default:
        return 'mp3';
    }
  }
}
